import { NavigateFunction } from "react-router";

import { PolicyStep } from "@covertree/new-business-graphql";
import {
  NewPolicyRoutes,
  OVERVIEW_PATH,
  SEQUOIA_PATH,
  getNewPolicyPath,
} from "./paths";

const policyStepToRouteMap: Record<PolicyStep, NewPolicyRoutes> = {
  [PolicyStep.Checkout]: NewPolicyRoutes.CHECKOUT,
  [PolicyStep.ClaimsCheck]: NewPolicyRoutes.CLAIMS_CHECK,
  [PolicyStep.ExtraCoverages]: NewPolicyRoutes.EXTRA_COVERAGES,
  [PolicyStep.HomeDetails]: NewPolicyRoutes.HOME_DETAILS, // Probably not needed but keep it just in case
  [PolicyStep.HomeOptions]: NewPolicyRoutes.UNDERWRITING_QUESTIONS,
  [PolicyStep.PersonalDetails]: NewPolicyRoutes.PERSONAL_DETAILS, // Probably not needed but keep it just in case
  [PolicyStep.PersonalizedPlans]: NewPolicyRoutes.PERSONALIZED_QUOTES,
  [PolicyStep.PolicyDetails]: NewPolicyRoutes.HOME_ADDRESS, // For Sequoia only
  [PolicyStep.PolicySummary]: NewPolicyRoutes.POLICY_SUMMARY,
  [PolicyStep.UnderwritingQuestions]: NewPolicyRoutes.UNDERWRITING_QUESTIONS,
};

const getRoutesOrder = (isRental: boolean) => [
  NewPolicyRoutes.SELECT_PRODUCT,
  ...(isRental ? [NewPolicyRoutes.UNIT_COUNT] : []),
  NewPolicyRoutes.HOME_ADDRESS,
  NewPolicyRoutes.HOME_DETAILS,
  NewPolicyRoutes.PERSONAL_DETAILS,
  NewPolicyRoutes.PERSONALIZED_QUOTES,
  NewPolicyRoutes.EXTRA_COVERAGES,
  NewPolicyRoutes.UNDERWRITING_QUESTIONS,
  NewPolicyRoutes.CLAIMS_CHECK,
  NewPolicyRoutes.CLAIMS_LIST,
  NewPolicyRoutes.POLICY_SUMMARY,
  NewPolicyRoutes.CHECKOUT,
];

export function recreateHistoryStackUpTo(
  navigate: NavigateFunction,
  step: PolicyStep,
  isRental: boolean
): NewPolicyRoutes {
  const routesOrderInFlow = getRoutesOrder(isRental);

  const currentRoute = policyStepToRouteMap[step];
  if (!currentRoute) {
    console.error(`No route mapped for step: ${step}`);
    return;
  }

  const currentRouteIndex = routesOrderInFlow.indexOf(currentRoute);
  if (currentRouteIndex === -1) {
    console.error(`Step ${step} not found in routes flow.`);
    return;
  }

  routesOrderInFlow
    .slice(0, currentRouteIndex)
    .forEach((route, index) =>
      navigate(getNewPolicyPath(route), { replace: index === 0 })
    );

  return routesOrderInFlow[currentRouteIndex];
}

export function handleCYQRedirection(
  navigate: NavigateFunction,
  step: PolicyStep,
  isBookConversionPolicy: boolean,
  isRental: boolean,
  hasManualUWAccepted: boolean,
  showSurplusLinesModal = false
) {
  const surplusLinesState = showSurplusLinesModal
    ? { showSurplusLinesFromCYQ: true }
    : {};

  // FYI In this case step will always be PolicyStep.Checkout, but we wanna make sure
  if (hasManualUWAccepted && step === PolicyStep.Checkout) {
    navigate(getNewPolicyPath(NewPolicyRoutes.CHECKOUT), {
      state: {
        hasManualUWAccepted: true,
        overviewPath: OVERVIEW_PATH,
        sequoiaPath: SEQUOIA_PATH,
        ...surplusLinesState,
      },
    });
    return;
  }
  if (isBookConversionPolicy && step === PolicyStep.PolicyDetails) {
    navigate(getNewPolicyPath(NewPolicyRoutes.HOME_ADDRESS), {
      replace: true,
      state: surplusLinesState,
    });
    return;
  }

  const destinationPath = recreateHistoryStackUpTo(navigate, step, isRental);

  navigate(getNewPolicyPath(destinationPath), {
    state: surplusLinesState,
  });
}
